import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { connect } from "react-redux";
import { store } from "../../../store/configureStore";
import { capitaliseText, scroll, trackEvent } from "../../../atlas-utils";
import { ActionTypes } from "../../../actions/_types";
import ReactTable from "../../../components/Periscope/NewTable/ReactTable";
import EntityCard from "../../../components/Periscope/NewTable/EntityCard";
import { CATALOGUE_PLATFORMS_LOGO } from "../../../client-config";
import { getBrandDetail, getPlatformDetailViaName } from "../../../helpers/periscope";
import EntityCell from "../../../components/Periscope/NewTable/EntityCell";
import EntityHead from "../../../components/Periscope/NewTable/EntityHead";
import SkeletonLoader from "../../../components/Periscope/SkeletonLoader";
import { fetchActivityLogLocationAvailabilityTimeSeries, periscopeFetchStoreStatus } from "../../../actions/periscope";
import StoreDetailView from "../../../components/Periscope/LocationAvailability/StoreDetailView";
import { ButtonIcon } from "../../../components/_commons/ButtonIcon";

const LocationAvailabilityTable = ({
	periscopeLocationAvailabilityState,
	selectedBrand,
	tableRef,
	periscopeLocationAvailabilityData,
	defaultPlatforms,
	refreshBtn,
	isMultibrandEnabled,
	login,
	configItems
}) => {
	const data = periscopeLocationAvailabilityData?.data?.objects?.data;
	const loading = periscopeLocationAvailabilityData?.loading;
	const error = periscopeLocationAvailabilityData?.error;
	const dataCount = periscopeLocationAvailabilityState.count;
	const limit = periscopeLocationAvailabilityState.limit;
	const offset = periscopeLocationAvailabilityState.offset;
	const bizId = login?.loggedInbizDetail?.id;

	const handlePagination = useCallback((page) => {
		const offset = (page - 1) * limit;
		store.dispatch({
			type: ActionTypes.LOCATION_AVAILABILITY_LIST_STATE_CHANGE,
			payload: { offset: offset }
		});
		if (tableRef.current) {
			scroll({ top: tableRef.current.offsetTop - 57, left: 0 });
		}
	}, []);

	const handlePageSize = useCallback((field, size) => {
		store.dispatch({
			type: ActionTypes.LOCATION_AVAILABILITY_LIST_STATE_CHANGE,
			payload: { limit: size.value, offset: 0 }
		});

		if (tableRef.current) {
			scroll({ top: tableRef.current.offsetTop - 57, left: 0 });
		}
	}, []);

	const dispatchSortFunction = (sort = null) => {
		if (sort === null) {
			store.dispatch({
				type: ActionTypes.LOCATION_AVAILABILITY_LIST_INITIAL_STATE_SORT
			});
		} else {
			store.dispatch({
				type: ActionTypes.LOCATION_AVAILABILITY_LIST_STATE_CHANGE,
				payload: sort
			});
		}
	};

	const handleClickSort = (column) => {
		if (column !== "location" && column !== "brand") {
			store.dispatch({
				type: ActionTypes.LOCATION_AVAILABILITY_LIST_STATE_CHANGE,
				payload: { offset: 0 }
			});
			const { field, order } = periscopeLocationAvailabilityState?.sort || {};
			let newOrder = "asc";
			if (field === column) {
				if (order === "asc") {
					newOrder = "desc";
				} else if (order === "desc") {
					newOrder = null;
				}
			}
			const sort = newOrder ? { sort: { field: column, order: newOrder } } : null;
			dispatchSortFunction(sort);
		}
	};

	const platformValues = useMemo(() => {
		return periscopeLocationAvailabilityState?.appliedFilters?.platform?.value
			? periscopeLocationAvailabilityState.appliedFilters.platform.value.split(",")
			: defaultPlatforms.split(",");
	}, [periscopeLocationAvailabilityState?.appliedFilters?.platform?.value, defaultPlatforms]);

	let newTable = ["location"];
	if (selectedBrand?.id === "all") {
		newTable.push("brand");
	}
	const tableData = useMemo(() => {
		return [...newTable, ...platformValues];
	}, [newTable, platformValues]);
	const columnsData = [...newTable, ...platformValues].map((col) => {
		return {
			id: col,
			render: () => {
				return (
					<>
						<EntityHead
							title={col}
							{...(col !== "location" &&
								col !== "brand" && {
									imgSrc:
										getPlatformDetailViaName(col, bizPlatforms)?.image ||
										CATALOGUE_PLATFORMS_LOGO[col],
									sort: col !== "location" && col !== "brand",
									handleSortClick: handleClickSort,
									sortOrder: periscopeLocationAvailabilityState.sort?.order,
									sortedField: periscopeLocationAvailabilityState.sort?.field
								})}
						/>
					</>
				);
			}
		};
	});
	const duplicateValues = useMemo(() => {
		let valuesPattern = [null, null, null, undefined, undefined, undefined];
		let indexInPattern = 0;
		return Array.from({ length: 9 }).map((_) => {
			let obj = {};
			tableData.forEach((key) => {
				obj[key] = valuesPattern[indexInPattern];
			});
			indexInPattern = (indexInPattern + 1) % valuesPattern.length;
			return obj;
		});
	}, [tableData, error]);
	const platformCount = `${String(100 / platformValues.length)}%`;

	const brands = configItems?.brands?.items;
	const rowspanned = ["location"];
	const renderToolTip = useCallback((value, rest, hover) => {
		return (
			<>
				{value === "true" || value == "false" ? (
					<div className="status-tool-desc">
						<StorefrontStatus value={value} rest={rest} hover={hover} />
					</div>
				) : (
					<StoreFrontDoesNotExist />
				)}
			</>
		);
	}, []);
	const bizPlatforms = configItems.bizPlatforms.items;

	const newColumns = useMemo(() => {
		return Object.keys(loading || error ? duplicateValues?.[0] || {} : (data && data.length > 0 && data?.[0]) || {})
			.map((obj) => {
				if (tableData.includes(obj)) {
					return {
						Header: (
							<EntityHead
								title={obj}
								{...(obj !== "location" &&
									obj !== "brand" && {
										sort: true,
										imgSrc:
											getPlatformDetailViaName(obj, bizPlatforms)?.image ||
											CATALOGUE_PLATFORMS_LOGO[obj],
										handleSortClick: handleClickSort,
										sortOrder: periscopeLocationAvailabilityState.sort?.order,
										sortedField: periscopeLocationAvailabilityState.sort?.field
									})}
							/>
						),
						accessor: obj,
						enableRowSpan: rowspanned.includes(obj),
						Cell: (props) => {
							if (props?.column?.id === "location") {
								return (
									<EntityCard
										loading={loading}
										currCell={props.column.id}
										error={error}
										title={props?.value?.name}
										{...(selectedBrand?.id !== "all"
											? { ratingText: true, star: true, rating: props?.value?.rating }
											: {})}
									/>
								);
							} else if (props?.column?.id === "brand") {
								const currentBrand = props?.value?.id && getBrandDetail(props?.value?.id, brands);

								return (
									<EntityCard
										loading={loading}
										error={error}
										ratingText
										displayIcon
										rating={props?.value?.rating}
										star
										brandText={currentBrand?.name}
										imgSrc={currentBrand?.image}
										currCell={props.column.id}
										title={props?.value?.name}
										iconColor={currentBrand?.color}
									/>
								);
							} else {
								const stringValue = JSON.stringify(props?.value?.is_available);
								const locationId = props?.row?.values?.location?.id;
								const locationName = props?.row?.values?.location?.name;
								const platformName = (props?.column?.id || "").toLowerCase();
								const brandId = isMultibrandEnabled ? props?.row?.values?.brand?.id || null : null;
								const rest = {
									locationId,
									locationName,
									platformName,
									isMultibrandEnabled,
									brandId,
									currCellValue: stringValue,
									bizId,
									currRowValue: props?.row?.values
								};

								return (
									<EntityCell
										loading={loading}
										error={error}
										styles={{ width: platformCount }}
										classes={
											stringValue == "true"
												? "true-state"
												: stringValue == "false"
													? "false-state"
													: "empty-state"
										}
										value={props?.value?.is_available}
										customToolTip={true}
										renderToolTip={renderToolTip}
										{...rest}
									/>
								);
							}
						}
					};
				}
				return null;
			})
			.filter(Boolean);
	}, [data, loading, tableData, brands, platformCount, error]);

	const sideBarOpen = periscopeLocationAvailabilityData?.graph?.sideBarOpen;

	return (
		<>
			{defaultPlatforms && defaultPlatforms.length > 0 ? (
				<>
					<StoreDetailView isFormOpen={sideBarOpen} />
					<div className="new-table">
						{tableData && tableData.length > 0 && (
							<ReactTable
								loading={loading}
								error={error}
								rowspanned={rowspanned}
								classes={`storefront-availabilty-table ${selectedBrand?.id !== "all" ? "brand-selected" : ""}`}
								data={loading || error ? duplicateValues : data}
								columns={newColumns}
								colsArray={columnsData}
								thStyles={{
									cols: ["location", "brand"],
									styles: {
										width: platformCount,
										textAlign: "end"
									}
								}}
								limit={limit}
								offset={offset}
								goToPage={handlePagination}
								setPageSize={handlePageSize}
								count={dataCount}
								fetchData={refreshBtn}
							/>
						)}
					</div>
				</>
			) : (
				<></>
			)}
		</>
	);
};

const mapStateToProps = (state) => ({
	configItems: state.configItems,
	login: state.login,
	isMultibrandEnabled: state.login?.loggedInbizDetail?.isMultibrandEnabled,
	periscopeLocationAvailabilityState: state.periscopeLocationAvailabilityState,
	periscopeLocationAvailabilityData: state.periscopeLocationAvailabilityData
});

export default connect(mapStateToProps)(LocationAvailabilityTable);

const StoreFrontDoesNotExist = () => <div className="no-store">This storefront does not exist</div>;

const StorefrontStatus = ({ value, rest, hover }) => {
	const [loading, setLoading] = useState(true);
	const [data, setData] = useState({});
	const isMounted = useRef(true);
	const fetchData = async () => {
		try {
			const platformName = rest.platformName;
			const loacationId = rest.locationId;
			const filters = [
				{ field: "platform", value: platformName },
				{ field: "action", value: "disable,enable" },
				{ field: "status", value: "success" },
				{ field: "created", value: "LAST_90_DAYS" }
			];
			const variables = {
				limit: 1,
				offset: 0,
				sort: {
					field: "created",
					order: "DESC"
				},
				locationIds: [loacationId],
				filters,
				...(rest.isMultibrandEnabled && rest.brandId ? { brandId: Number(rest.brandId) } : {})
			};
			if (isMounted.current) {
				setLoading(true);
			}
			const resp = await periscopeFetchStoreStatus(variables);
			if (isMounted.current) {
				setData(resp);
			}
		} catch (err) {
		} finally {
			isMounted.current && setLoading(false);
		}
	};

	const timeoutRef = useRef(null);
	useEffect(() => {
		if (hover) {
			isMounted.current = true;

			timeoutRef.current = setTimeout(() => {
				fetchData();
			}, 220);
		} else {
			isMounted.current = false;
			clearTimeout(timeoutRef.current);
		}
		return () => {
			clearTimeout(timeoutRef.current);
			isMounted.current = false;
		};
	}, [hover]);

	let status = "";
	let action = "";
	const actionState = data && (data?.locationPlatform?.state || "").toLowerCase();
	if (data?.locationPlatform?.state) {
		action =
			actionState == "enabled" || actionState == "disabled"
				? actionState == "enabled"
					? "Online"
					: "Offline"
				: "";
	}
	if (data?.updated) {
		const date = moment(data?.updated);
		const now = moment();

		if (date.year() === now.year()) {
			status = date.format("DD MMM - hh:mm A");
		} else {
			status = date.format("DD MMM YYYY - hh:mm A");
		}
	}

	let reason = "";
	if (data?.reasonCode) {
		const formatted = data.reasonCode.split("_").map(capitaliseText).join(" ");
		reason = formatted.length > 35 ? formatted.slice(0, 35) + "..." : formatted;
	}

	const sideBarVariables = {
		bizId: String(rest.bizId),
		...(rest.isMultibrandEnabled && rest.brandId ? { brandId: String(rest.brandId) } : { brandId: "all" }),
		locationId: rest.locationId,
		platform: rest.platformName,
		time: "last_7_days"
	};

	const handleClick = async () => {
		const platformName = rest.platformName;
		const loacationId = rest.locationId;
		const filters = [
			{ field: "platform", value: platformName },
			{ field: "action", value: "disable,enable" },
			{ field: "status", value: "success" },
			{ field: "created", value: "LAST_90_DAYS" }
		];
		const tooltipVariables = {
			limit: 1,
			offset: 0,
			sort: {
				field: "created",
				order: "DESC"
			},
			locationIds: [loacationId],
			filters,
			...(rest.isMultibrandEnabled && rest.brandId ? { brandId: Number(rest.brandId) } : {})
		};
		trackEvent("storefront_tooltip_clicked", {
			platform_name: capitaliseText(platformName),
			storefront_status: action.toLowerCase(),
			drawer_entry_point: "from_tooltip"
		});

		store.dispatch({
			type: ActionTypes.LOCATION_AVAILABILITY_PERISCOPE_GRAPH_DATA,
			payload: {
				sideBarOpen: true,
				platform: rest.platformName,
				currRowValue: rest.currRowValue,
				variables: sideBarVariables,
				storeAction: data,
				storeActionLoading: loading,
				storeActionVariables: tooltipVariables
			}
		});
		fetchActivityLogLocationAvailabilityTimeSeries(sideBarVariables, "location");
	};

	return (
		<div className="storefront-offline" onClick={() => handleClick()}>
			<div className="main-text">
				{value == "false" ? (
					<div className="desc-title">
						<div>
							Storefront is <span className="c-b02b2b font--semibold ">offline</span>
						</div>
						<ButtonIcon icon="dropdown-arrow" color="#363636" classes="dropdown-icon" />
					</div>
				) : (
					<div className="desc-title">
						<div>
							Storefront is <span className="c-0da125 font--semibold ">Online</span>
						</div>
						<ButtonIcon icon="dropdown-arrow" color="#363636" classes="dropdown-icon" />
					</div>
				)}
			</div>
			{(loading || status || reason) && (
				<div className="add-info ">
					{loading ? (
						<div className="loading">
							<SkeletonLoader classname={"large"} />
							{value == "false" && <SkeletonLoader classname={"small"} />}
						</div>
					) : (
						<>
							{action && status && (
								<div className="status-txt">
									<span className="static-txt">Last Action: {action} on</span>
									<span className="dynamic-txt">{status}</span>
								</div>
							)}
							{value == "false" && reason && <div className="reason">"{reason}"</div>}
						</>
					)}
				</div>
			)}
			{!loading && !(data && Object.prototype.hasOwnProperty.call(data, "updated")) && (
				<div className="no-data static-txt">No actions in the last 90 days</div>
			)}
		</div>
	);
};
